import React from "react";

import type { ContactImportDTO, ContactImportRowDTO, ContactPushDTO, Party } from "../../data/contactsApi";
import {
  ACTION_LABEL,
  ACTION_STYLE,
  ROW_STATUS_LABEL,
  ROW_STATUS_STYLE,
  hasBeenPushed,
  warningCount,
} from "../../domain/contacts";
import { Callout } from "../../components/Callout";
import { cn } from "../../components/cn";

/**
 * What the sheet would do, in four numbers — and once it has been pushed, what
 * it did.
 *
 * The counts before the rows because this is the question a CA actually has —
 * "did my forty edits land, and did I break anything" — and two hundred rows is
 * the wrong shape to answer it in.
 *
 * The tiles swap wholesale after a push rather than sitting alongside new ones.
 * "To be added: 12" next to "Added: 9" is two numbers about the same twelve
 * contacts, and reading it wrong means believing twenty-one were involved.
 */

function Tile({ label, count, style }: { label: string; count: number; style: string }) {
  return (
    <div className={cn("flex flex-col gap-0.5 rounded-md px-3 py-2", style)}>
      <span className="text-xl font-semibold tabular-nums">{count}</span>
      <span className="text-xs font-medium">{label}</span>
    </div>
  );
}

export function ContactImportSummary({
  summary,
  rows,
  party,
  push,
  pushing,
}: {
  summary: ContactImportDTO;
  rows: ContactImportRowDTO[];
  party: Party;
  /** Only from a commit response — a plain read has no batch to report. */
  push?: ContactPushDTO | null;
  pushing?: boolean;
}) {
  const noun = party === "vendor" ? "vendor" : "customer";
  const warnings = warningCount(rows);
  const toPush = summary.new_rows + summary.changed_rows;

  if (hasBeenPushed(summary)) {
    return (
      <PushOutcome
        summary={summary}
        push={push ?? null}
        pushing={pushing === true}
        toPush={toPush}
        noun={noun}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Tile label={ACTION_LABEL.new} count={summary.new_rows} style={ACTION_STYLE.new} />
        <Tile label={ACTION_LABEL.update} count={summary.changed_rows} style={ACTION_STYLE.update} />
        <Tile label={ACTION_LABEL.invalid} count={summary.invalid_rows} style={ACTION_STYLE.invalid} />
        <Tile label={ACTION_LABEL.unchanged} count={summary.unchanged_rows} style={ACTION_STYLE.unchanged} />
      </div>

      {/*
        The one number that matters for cost: unchanged rows are never sent, so
        a 200-row sheet with three edits is three Zoho calls, not 200.
      */}
      <p className="text-sm text-muted-foreground">
        {toPush === 0
          ? "Nothing in this sheet would change anything in Zoho."
          : `${toPush} ${toPush === 1 ? noun : `${noun}s`} would be sent to Zoho. The other ${
              summary.total_rows - toPush
            } cost nothing.`}
      </p>

      {summary.invalid_rows > 0 && (
        <Callout variant="warning">
          {summary.invalid_rows} {summary.invalid_rows === 1 ? "row needs" : "rows need"} fixing before
          {summary.invalid_rows === 1 ? " it goes" : " they go"} anywhere. Everything else is unaffected - fix
          those lines in the sheet and upload it again.
        </Callout>
      )}

      {warnings > 0 && (
        <Callout variant="warning">
          {warnings} {warnings === 1 ? "row is" : "rows are"} flagged but not blocked. Read the note on each
          before confirming.
        </Callout>
      )}

      {summary.already_pushed_at !== null && (
        <Callout variant="warning">
          This exact file was already pushed on {summary.already_pushed_at}. Zoho has no way to recognise a
          repeat, so pushing it again would add every new {noun} on it a second time.
        </Callout>
      )}
    </div>
  );
}

/**
 * What the push actually did.
 *
 * Progress and result are the same component because they are the same numbers.
 * A separate progress bar that vanishes on completion means the CA watching it
 * finish sees the answer replaced at the exact moment it becomes worth reading.
 */
function PushOutcome({
  summary,
  push,
  pushing,
  toPush,
  noun,
}: {
  summary: ContactImportDTO;
  push: ContactPushDTO | null;
  pushing: boolean;
  toPush: number;
  noun: string;
}) {
  const settled = summary.created_rows + summary.updated_rows + summary.failed_rows;
  const done = summary.status === "done";

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-3 gap-2">
        <Tile label={ROW_STATUS_LABEL.created} count={summary.created_rows} style={ROW_STATUS_STYLE.created} />
        <Tile label={ROW_STATUS_LABEL.updated} count={summary.updated_rows} style={ROW_STATUS_STYLE.updated} />
        <Tile label={ROW_STATUS_LABEL.failed} count={summary.failed_rows} style={ROW_STATUS_STYLE.failed} />
      </div>

      {/*
        A count rather than a bar. The total is known, and "31 of 38" answers
        "how much longer" as well as a bar does while also surviving the push
        being paused, which a bar animating to nowhere does not.
      */}
      <p className="text-sm text-muted-foreground">
        {pushing
          ? `Sending… ${settled} of ${toPush} done.`
          : done
            ? `Finished. ${settled} of ${toPush} ${toPush === 1 ? noun : `${noun}s`} sent to Zoho.`
            : `Stopped at ${settled} of ${toPush}. Nothing further has been sent.`}
      </p>

      {push?.halted_reason != null && <Callout variant="warning">{push.halted_reason}</Callout>}

      {/* Two different pieces of news, and the old copy told the first one in
          both cases — "already sent are in Zoho" over a push that had sent
          nothing at all. */}
      {!pushing && !done && push?.halted !== true && (
        <Callout variant="info">
          {summary.created_rows + summary.updated_rows === 0
            ? `This push did not finish, and no ${noun}s have reached Zoho yet. Continuing carries on from where it stopped.`
            : `This push did not finish. The ${noun}s already sent are in Zoho - continuing picks up from where it stopped rather than starting again.`}
        </Callout>
      )}

      {done && summary.failed_rows > 0 && (
        <Callout variant="warning">
          Zoho refused {summary.failed_rows} {summary.failed_rows === 1 ? "row" : "rows"}. Each one carries its
          reason below. Everything else is in the client's books.
        </Callout>
      )}
    </div>
  );
}
